import axios from "axios";
import { getOutsourceForJob } from "./jobApi";

// ===== Outsource Jobs =====

/**
 * Get all outsource jobs.
 * GET /v1/outsource
 * @param {{ page, count, status, vendor_id, service_type }} params
 */
export const getOutsourceJobs = async (params = {}) => {
  const response = await axios.get("outsource", { params });
  return response.data;
};

/**
 * Get outsource job by ID (full detail with deliveries, status_history).
 * GET /v1/outsource/:id
 */
export const getOutsourceJob = async (id) => {
  const response = await axios.get(`outsource/${id}`);
  return response.data;
};

/**
 * Create a new outsource job.
 * POST /v1/outsource
 * @param {{ job_id, vendor_id, stage_id, service_type, specifications, artwork_url, sent_quantity, expected_date, quoted_rate, notes }} data
 */
export const createOutsourceJob = async (data) => {
  const response = await axios.post("outsource", data);
  return response.data;
};

/**
 * Update outsource job fields.
 * PUT /v1/outsource/:id
 * Updatable: specifications, artwork_url, specification_doc, expected_date, quoted_rate, transport_cost, other_charges, notes
 */
export const updateOutsourceJob = async (id, data) => {
  const response = await axios.put(`outsource/${id}`, data);
  return response.data;
};

// ===== Status Updates =====

/**
 * Update outsource job status.
 * PUT /v1/outsource/:id/status
 * @param {{ status, notes }} data
 */
export const updateOutsourceStatus = async (id, data) => {
  const response = await axios.put(`outsource/${id}/status`, data);
  return response.data;
};

/**
 * Mark outsource job as sent to vendor.
 * PUT /v1/outsource/:id/send
 * @param {{ tracking_number, notes }} data
 */
export const markAsSent = async (id, data) => {
  const response = await axios.put(`outsource/${id}/send`, data);
  return response.data;
};

// ===== Deliveries =====

/**
 * Record a delivery (partial or full).
 * POST /v1/outsource/:id/delivery
 * @param {{ received_qty, rejected_qty, qc_status, qc_notes, challan_no, challan_date, notes }} data
 */
export const recordDelivery = async (outsourceJobId, data) => {
  const response = await axios.post(
    `outsource/${outsourceJobId}/delivery`,
    data
  );
  return response.data;
};

// ===== QC & Completion =====

/**
 * Update QC status on an outsource job.
 * PUT /v1/outsource/:id/qc
 * @param {{ qc_status, qc_notes }} data  — qc_status: "passed" | "failed" | "partial_pass"
 */
export const updateQC = async (id, data) => {
  const response = await axios.put(`outsource/${id}/qc`, data);
  return response.data;
};

/**
 * Complete an outsource job (only when qc_passed).
 * PUT /v1/outsource/:id/complete
 * @param {{ actual_rate, vendor_invoice_no, vendor_invoice_date, notes }} data
 */
export const completeOutsource = async (id, data) => {
  const response = await axios.put(`outsource/${id}/complete`, data);
  return response.data;
};

// ===== Reports / Queries =====

/**
 * Get overdue outsource jobs.
 * GET /v1/outsource/overdue
 */
export const getOverdueJobs = async () => {
  const response = await axios.get("outsource/overdue");
  return response.data;
};

// ===== Vendors =====

/**
 * Get vendors that provide a specific service type.
 * GET /v1/outsource/vendors/service/:serviceType
 */
export const getVendorsByService = async (serviceType) => {
  const response = await axios.get(
    `outsource/vendors/service/${serviceType}`
  );
  return response.data;
};

/**
 * Get vendor performance metrics.
 * GET /v1/outsource/vendors/:vendorId/performance
 * @param {{ start_date, end_date }} params  (required)
 */
export const getVendorPerformance = async (vendorId, params = {}) => {
  const response = await axios.get(
    `outsource/vendors/${vendorId}/performance`,
    { params }
  );
  return response.data;
};

// Re-export from jobApi
export { getOutsourceForJob };
